import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  DollarSign,
  Package,
  FileText,
  Calendar,
  Building2,
  RefreshCw,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getConsolidatedPLReport,
  getPayrollCostReport,
  getInventoryValuationReport
} from '../../api/reports';
import BlurredValue from '../../components/common/BlurredValue';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const FinancialReportsHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plReport, setPLReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);

  // Default to current month
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();

  const [dateRange, setDateRange] = useState({
    start: startOfMonth.split('T')[0],
    end: endOfMonth.split('T')[0]
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plData, invData] = await Promise.all([
        getConsolidatedPLReport(dateRange.start, dateRange.end),
        getInventoryValuationReport()
      ]);
      setPLReport(plData);
      setInventoryReport(invData);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const renderKPI = (label, value, icon, color = 'blue', isNegative = false) => (
    <div className={`bg-white rounded-lg shadow-sm p-4 border-2 border-${color}-500`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-sm font-medium text-gray-600">{label}</span>
      </div>
      <p className={`text-xl font-bold ${isNegative ? 'text-red-600' : ''}`}>
        <BlurredValue value={value} />
      </p>
    </div>
  );

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  const netProfit = plReport?.summary?.net_profit || 0;
  const isProfit = netProfit >= 0;

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
                Financial Reports
              </h1>
              <p className="text-sm text-gray-500">Overview and detailed reports</p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              />
              <span>to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              />
              <button onClick={fetchData} className="p-2 border rounded-lg hover:bg-gray-50">
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* KPI Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {renderKPI(
            'Total Revenue',
            formatCurrency(plReport?.summary?.total_revenue),
            <DollarSign className="w-5 h-5 text-green-600" />,
            'green'
          )}
          {renderKPI(
            'Total Expenses',
            formatCurrency(plReport?.summary?.total_expenses),
            <TrendingDown className="w-5 h-5 text-red-600" />,
            'red'
          )}
          {renderKPI(
            'Net ' + (isProfit ? 'Profit' : 'Loss'),
            formatCurrency(Math.abs(netProfit)),
            isProfit ? (
              <TrendingUp className="w-5 h-5 text-green-600" />
            ) : (
              <TrendingDown className="w-5 h-5 text-red-600" />
            ),
            isProfit ? 'green' : 'red',
            !isProfit
          )}
          {renderKPI(
            'Inventory Value',
            formatCurrency(inventoryReport?.summary?.total_retail_value),
            <Package className="w-5 h-5 text-purple-600" />,
            'purple'
          )}
        </div>

        {/* Report Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <button
            onClick={() => navigate('/reports/pl-consolidated')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold">Consolidated P&L</h3>
                <p className="text-sm text-gray-500">All branches combined</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              View profit & loss across all branches with monthly trends and category breakdowns.
            </p>
          </button>

          <button
            onClick={() => navigate('/reports/pl-branch')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold">Branch P&L</h3>
                <p className="text-sm text-gray-500">Per-branch reports</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Detailed profit & loss for individual branches with performance metrics.
            </p>
          </button>

          <button
            onClick={() => navigate('/reports/payroll-cost')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold">Payroll Cost</h3>
                <p className="text-sm text-gray-500">Employee expenses</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Payroll costs by employee and month, including PAYE and pension contributions.
            </p>
          </button>

          <button
            onClick={() => navigate('/reports/inventory-valuation')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold">Inventory Valuation</h3>
                <p className="text-sm text-gray-500">Stock value analysis</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Current inventory value at cost and retail prices by branch and product.
            </p>
          </button>

          <button
            onClick={() => navigate('/reports/sales-book')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <FileText className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold">Sales Book</h3>
                <p className="text-sm text-gray-500">Detailed sales log</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Complete sales record with daily summaries and payment method breakdown.
            </p>
          </button>

          <button
            onClick={() => navigate('/reports/statement-of-account')}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow text-left"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-teal-100 rounded-lg">
                <FileText className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold">Statement of Account</h3>
                <p className="text-sm text-gray-500">General Ledger</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              General cash flow ledger including sales, credit collections, expenses, and payroll.
            </p>
          </button>
        </div>

        {/* Branch Performance */}
        {plReport?.by_branch?.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Branch Performance
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Branch</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Sales</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Revenue</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Gross Profit</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {plReport.by_branch.map((branch) => (
                    <tr
                      key={branch.branch_id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/reports/pl-branch?branchId=${branch.branch_id}`)}
                    >
                      <td className="px-4 py-3 font-medium">{branch.branch_name}</td>
                      <td className="px-4 py-3 text-center">{branch.sale_count}</td>
                      <td className="px-4 py-3 text-right">
                        <BlurredValue value={formatCurrency(branch.revenue)} />
                      </td>
                      <td className="px-4 py-3 text-right text-green-600 font-semibold">
                        <BlurredValue value={formatCurrency(branch.gross_profit)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialReportsHub;
